import { useMemo, useRef } from 'react';
import type { DeepKeys } from '@tanstack/react-form';

import { getFieldRenderer } from './internal/fieldMapper';
import { schemaToZod } from './internal/schemaToZod';
import type { JSONSchema } from './types';

export interface JsonSchemaFieldFormComponentProps<TFormData, TName extends DeepKeys<TFormData>> {
  /**
   * The field name in the form (e.g., 'threshold', 'user.age').
   */
  name: TName;

  /**
   * JSON Schema defining the field type, constraints, and metadata.
   * Validation is automatically derived from this schema.
   */
  schema: JSONSchema;

  /**
   * Whether the field is required (shows asterisk via RequiredLabel).
   */
  required?: boolean;
}

/**
 * Creates a JsonSchemaField form component bound to a specific form context.
 *
 * This factory function allows the component to be used with any form hook
 * by accepting the `useFormContext` from that hook's contexts.
 *
 * @param useFormContext - The useFormContext hook from createFormHookContexts()
 * @returns A form component that renders JSON Schema-driven fields with automatic validation
 *
 * @example
 * ```tsx
 * const { useFormContext } = createFormHookContexts();
 * const JsonSchemaField = createJsonSchemaFieldFormComponent(useFormContext);
 *
 * const { useAppForm } = createFormHook({
 *   formComponents: { JsonSchemaField },
 *   // ...
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createJsonSchemaFieldFormComponent(useFormContext: () => any) {
  /**
   * A form component that renders a JSON Schema-driven field with automatic validation.
   *
   * Unlike the render-only `JsonSchemaField`, this component:
   * 1. Wraps `form.AppField` internally
   * 2. Automatically converts the JSON Schema to Zod validation via `schemaToZod()`
   * 3. Renders the appropriate field UI based on `schema.type`
   *
   * This eliminates the need for consumers to manually wire up validation.
   *
   * @example
   * ```tsx
   * // Inside a form.AppForm context:
   * <form.JsonSchemaField
   *   name="threshold"
   *   schema={{ type: 'number', title: 'Threshold', minimum: 0, maximum: 100 }}
   * />
   * ```
   */
  /**
   * Inner component for rendering object schemas.
   * Separated to ensure consistent hook call order (object schemas call useFormContext inside).
   */
  function ObjectSchemaField<TFormData, TName extends DeepKeys<TFormData>>({
    name,
    schema,
    required,
  }: JsonSchemaFieldFormComponentProps<TFormData, TName>) {
    const form = useFormContext();
    const fieldRenderer = getFieldRenderer('object');
    if (!fieldRenderer) {
      throw new Error('Object field renderer not registered');
    }
    // Pass the form context directly (not the hook) to avoid calling hooks inside render functions
    const renderField = fieldRenderer(schema, name as string, {
      required,
      useFormContext,
      formContext: form,
    });
    // Call as a plain function to get ReactNode — do NOT render as <Component />.
    // Rendering as JSX would create a new component type on every render (since
    // renderField is a new closure each time), causing React to unmount/remount
    // the entire subtree and lose input focus.
    return <>{renderField(null)}</>;
  }

  /**
   * Inner component for rendering array schemas.
   * Arrays require mode="array" on the Field component for TanStack Form's array operations.
   */
  function ArraySchemaField<TFormData, TName extends DeepKeys<TFormData>>({
    name,
    schema,
    required,
  }: JsonSchemaFieldFormComponentProps<TFormData, TName>) {
    const form = useFormContext();
    const zodSchema = schemaToZod(schema);
    const itemSchema = schema.items as JSONSchema | undefined;

    // Stable refs so keys/counters survive the renderer factory being re-called each render
    const stableKeysRef = useRef<string[]>([]);
    const keyCounterRef = useRef(0);
    const newlyAddedIndexRef = useRef<number | null>(null);

    const fieldRenderer = getFieldRenderer('array');
    if (!fieldRenderer) {
      throw new Error('Array field renderer not registered');
    }

    // Pass the form context directly (not the hook) to avoid calling hooks inside render functions
    const renderField = fieldRenderer(schema, name as string, {
      required,
      useFormContext,
      formContext: form,
      stableKeysRef,
      keyCounterRef,
      newlyAddedIndexRef,
    });

    // TanStack Form's field-level onChange on mode="array" only fires on
    // add/remove (pushValue/removeValue), not when individual items are edited.
    // onChangeListenTo tells it to also re-run when these child fields change,
    // which is needed for cross-item validations like uniqueItems.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const arrayValue: unknown[] = (() => {
      const parts = String(name)
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let val: unknown = form.state.values;
      for (const part of parts) {
        if (val === null || val === undefined || typeof val !== 'object') {
          return [];
        }
        val = (val as Record<string, unknown>)[part];
      }
      return Array.isArray(val) ? val : [];
    })();

    const childFieldNames = useMemo(() => {
      if (!itemSchema || arrayValue.length === 0) {
        return [] as string[];
      }
      const names: string[] = [];
      for (let i = 0; i < arrayValue.length; i++) {
        const itemName = `${String(name)}[${i}]`;
        if (itemSchema.type === 'object' && itemSchema.properties) {
          for (const propName of Object.keys(itemSchema.properties as Record<string, JSONSchema>)) {
            names.push(`${itemName}.${propName}`);
          }
        } else {
          names.push(itemName);
        }
      }
      return names;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, itemSchema, arrayValue.length]);

    return (
      <form.AppField
        name={name}
        mode="array"
        validators={{
          onChange: zodSchema,
          onChangeListenTo: childFieldNames as Array<DeepKeys<TFormData>>,
        }}
      >
        {renderField}
      </form.AppField>
    );
  }

  /**
   * Inner component for rendering primitive schemas (non-object types).
   * Separated to ensure consistent hook call order.
   */
  function PrimitiveSchemaField<TFormData, TName extends DeepKeys<TFormData>>({
    name,
    schema,
    required,
    schemaType,
  }: JsonSchemaFieldFormComponentProps<TFormData, TName> & { schemaType: string }) {
    const form = useFormContext();
    const zodSchema = schemaToZod(schema);

    const fieldRenderer = getFieldRenderer(schemaType);
    if (!fieldRenderer) {
      throw new Error(`Unsupported JSON Schema type: ${schemaType}`);
    }

    const renderField = fieldRenderer(schema, name as string, { required, useFormContext });

    return (
      <form.AppField
        name={name}
        validators={{
          onChange: zodSchema,
        }}
      >
        {renderField}
      </form.AppField>
    );
  }

  return function JsonSchemaFieldFormComponent<TFormData, TName extends DeepKeys<TFormData>>({
    name,
    schema,
    required,
  }: JsonSchemaFieldFormComponentProps<TFormData, TName>) {
    const schemaType = typeof schema.type === 'string' ? schema.type : undefined;

    if (!schemaType) {
      throw new Error('JSON Schema must have a type property');
    }

    // Object types are rendered by a separate component to maintain consistent hook order.
    // ObjectSchemaField calls useFormContext inside the ObjectFieldRenderer,
    // while PrimitiveSchemaField calls it at the component level.
    if (schemaType === 'object') {
      return <ObjectSchemaField name={name} schema={schema} required={required} />;
    }

    // Array types need mode="array" on the Field component for TanStack Form's array operations.
    if (schemaType === 'array') {
      return <ArraySchemaField name={name} schema={schema} required={required} />;
    }

    return <PrimitiveSchemaField name={name} schema={schema} required={required} schemaType={schemaType} />;
  };
}
